import React from "react";
import { TooltipHost } from "@fluentui/react/lib/Tooltip";
import CryptoJS from "crypto-js";
import "./CustomPlans.css";
import { MyContext } from "../../App";
import { RequestLicense } from "./RequestLicense";
import { RequestLicenseEnterprise } from "./RequestLicenseEnterprise";
import { DefaultButton } from "@fluentui/react/lib/Button";
import { Dialog, DialogContent } from "@mui/material";

const tooltipStyles = {
  root: {
    display: "inline-block",
  },
};

const calloutProps = {
  gapSpace: 0,
};

const IpTokenCount = [
  "25241198af9c52",
  "843b85132fe7ea",
  "6a981cfd695563",
  "1840068c4be068",
];

const CustomPlans = ({ AppName }) => {
  const [openStandardpopup, setOpenStandardpopup] = React.useState(false);
  const SiteName = React.useContext(MyContext);

  let isFromIndia = false;
  try {
    const cachedIp = localStorage.getItem("ipInfo");
    if (cachedIp) {
      const parsed = JSON.parse(cachedIp);
      isFromIndia = parsed?.country === "IN";
    }
  } catch (e) {}

  React.useEffect(() => {
    getData();
    let textvarcolor;
    let bgmainvarcolor;
    let bgsecondvarcolor;
    let darkbtnvarcolor;
    let btnBgColor;
    let topborder;
    if (SiteName === "HR365") {
      textvarcolor = "#1f39d4";
      bgmainvarcolor = "";
      bgsecondvarcolor = "#1959e3";
      darkbtnvarcolor = "#2323ce";
      topborder = "1vw";
      btnBgColor = "linear-gradient(135deg, #1285f5 24%, #2323ce 80%)";
    } else {
      textvarcolor = "#dd1077";
      bgmainvarcolor = "#f31c88";
      bgsecondvarcolor = "#c10161";
      darkbtnvarcolor = "#f8faff";
      topborder = "2.3vw";
      btnBgColor = "linear-gradient(135deg, #1285f5 24%, #2323ce 80%)";
    }
    document.documentElement.style.setProperty("--text-color", textvarcolor);
    document.documentElement.style.setProperty("--bg-main-color", bgmainvarcolor);
    document.documentElement.style.setProperty("--bg-second-color", bgsecondvarcolor);
    document.documentElement.style.setProperty("--dark-button-color", darkbtnvarcolor);
    document.documentElement.style.setProperty("--button-bg-color", btnBgColor);
    document.documentElement.style.setProperty("--top-for-line", topborder);
  }, []);

  async function getIpInfo() {
    // 1. Try ipinfo.io tokens sequentially
    for (let i = 0; i < IpTokenCount.length; i++) {
      const token = IpTokenCount[i];
      try {
        const response = await fetch(`https://ipinfo.io/json?token=${token}`);
        if (!response.ok || response.status === 429) continue;

        const data = await response.json();
        if (data && data.country && !data.error && data.status !== 429) {
          isFromIndia = data.country === "IN";
          localStorage.setItem("ipInfo", JSON.stringify(data));
          return data;
        }
      } catch (error) {}
    }

    // 2. Free Fallback A: ipapi.co (no token required)
    try {
      const response = await fetch("https://ipapi.co/json/");
      if (response.ok) {
        const data = await response.json();
        const countryCode = data?.country_code || data?.country;
        if (countryCode) {
          const standardizedData = { country: countryCode };
          isFromIndia = countryCode === "IN";
          localStorage.setItem("ipInfo", JSON.stringify(standardizedData));
          return standardizedData;
        }
      }
    } catch (fallbackAError) {}

    // 3. Free Fallback B: get.geojs.io (no token required)
    try {
      const response = await fetch("https://get.geojs.io/v1/ip/country.json");
      if (response.ok) {
        const data = await response.json();
        if (data && data.country) {
          const standardizedData = { country: data.country };
          isFromIndia = data.country === "IN";
          localStorage.setItem("ipInfo", JSON.stringify(standardizedData));
          return standardizedData;
        }
      }
    } catch (fallbackBError) {}

    return null;
  }

  function formatDynamicFeatureText(feature, plan) {
    if (!feature) return "";

    const addOn = plan?.AddOns?.[0];
    const showINR = isFromIndia && Boolean(plan?.priceINR);

    let formattedPrice = "";
    if (showINR) {
      const inrVal =
        addOn?.monthlyINR ||
        (addOn?.priceINR ? Math.round(Number(addOn.priceINR) / 12) : null);
      if (inrVal !== undefined && inrVal !== null && inrVal !== "") {
        formattedPrice = `₹${Number(inrVal).toLocaleString("en-IN")}`;
      }
    } else {
      const usdVal = addOn?.monthlyUSD || addOn?.price;
      if (usdVal !== undefined && usdVal !== null && usdVal !== "") {
        formattedPrice = `$${usdVal}`;
      }
    }

    let updated = feature;

    if (formattedPrice) {
      updated = updated.replace(/\[PRICE-ADDON\]/gi, formattedPrice);
    }

    if (showINR && addOn) {
      const monthlyINR =
        addOn.monthlyINR ||
        (addOn.priceINR ? Math.round(Number(addOn.priceINR) / 12) : null);
      const usersCount = addOn.noOfUsers ? parseInt(addOn.noOfUsers) : 10;
      const perUserMonthly = monthlyINR ? Math.round(monthlyINR / usersCount) : null;

      if (perUserMonthly) {
        updated = updated.replace(
          /(?:Additional users at|Add-on)\s+\$\d+(?:\.\d+)?\/user/gi,
          `Additional users at ₹${perUserMonthly.toLocaleString("en-IN")}/user`
        );
      }

      if (monthlyINR) {
        updated = updated.replace(
          /Add-on\s+\$\d+(?:\.\d+)?\s+for every\s+(\d+)\s+users/gi,
          (match, p1) => {
            const count = parseInt(p1);
            const cost =
              count === usersCount
                ? monthlyINR
                : Math.round((monthlyINR / usersCount) * count);
            return `Add-on ₹${cost.toLocaleString("en-IN")} for every ${count} users`;
          }
        );
      }
    }

    return updated;
  }

  async function getData() {
    try {
      await getIpInfo();

      let plans = null;

      // 1. Primary Call: Apps365 WordPress REST API
      try {
        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), 10000);
        const cacheBuster = Date.now();

        const response = await fetch(
          `https://www.apps365.com/wp-json/external-api/v1/products?t=${cacheBuster}`,
          {
            method: "GET",
            headers: {
              Accept: "application/json",
            },
            signal: controller.signal,
          }
        );

        clearTimeout(timeout);

        if (response.ok) {
          const item = await response.json();
          const list =
            item?.data?.value ||
            item?.value ||
            item?.d?.results ||
            item?.data ||
            (Array.isArray(item) ? item : []);

          plans = list.find(
            (c) =>
              c?.ProductName &&
              AppName &&
              c.ProductName.trim().toLowerCase() === AppName.trim().toLowerCase()
          );

          if (plans) {
            console.log("Plans loaded successfully from Apps365 WordPress REST API.");
          }
        } else {
          console.warn(`Apps365 WordPress API returned status: ${response.status}`);
        }
      } catch (wpError) {
        console.warn("Apps365 WordPress API request failed:", wpError);
      }

      // 2. Fallback: Power Automate flow
      if (!plans) {
        console.warn("Invoking fallback flow...");
        try {
          const fallbackUrl =
            "https://defaultdb23acafbe244d519d6e5a51626269.7c.environment.api.powerplatform.com:443/powerautomate/automations/direct/workflows/b562b2e400d448a0ada797c66e8c223c/triggers/manual/paths/invoke?api-version=1&sp=%2Ftriggers%2Fmanual%2Frun&sv=1.0&sig=bBdjf8dkuY4Nu44uv3l-HEYjt26o8TQUCRpJ8uuL2FU";

          const fallbackResponse = await fetch(fallbackUrl, { method: "GET" });

          if (fallbackResponse.ok) {
            const fallbackItem = await fallbackResponse.json();
            const list =
              fallbackItem?.d?.results ||
              fallbackItem?.data?.value ||
              fallbackItem?.value ||
              (Array.isArray(fallbackItem) ? fallbackItem : []);

            plans = list.find(
              (c) =>
                c?.ProductName &&
                AppName &&
                c.ProductName.trim().toLowerCase() === AppName.trim().toLowerCase()
            );
          }
        } catch (fallbackError) {
          console.error("Fallback API also failed:", fallbackError);
        }
      }

      if (!plans) {
        console.error("No plans found for this app:", AppName);
        return;
      }

      let UserBased = "No";
      let LiteUserBased = "No";
      let PromoPlan = "No";

      if (plans?.Data) {
        try {
          const d = JSON.parse(plans.Data);
          UserBased = d?.UserBased || "No";
          LiteUserBased = d?.LiteUser || "No";
          PromoPlan = d?.PromoPlan || "No";
        } catch (e) {}
      }

      let parsedData = parseAndCombinePlans(plans);

      // Remove Standard tier for all products except Helpdesk 365
      const isHelpdesk = AppName?.trim()?.toLowerCase() === "helpdesk 365";
      if (!isHelpdesk) {
        parsedData = parsedData.filter((plan) => plan.title !== "Standard");
      }

      const isCLM =
        AppName?.trim()?.toLowerCase() === "clm 365 - contract management";
      if (isCLM) {
        parsedData = parsedData.filter((plan) => plan.title !== "Plus");
      }

      const container = document.getElementById("pricing-widget-container");
      if (!container) return;
      container.innerHTML = "";

      const validData = parsedData.filter(
        (item) =>
          (item.price !== undefined &&
            item.subPrice !== undefined &&
            item.TotalPrice !== undefined &&
            Array.isArray(item.plans) &&
            item.plans.length > 0 &&
            item.plans.some((plan) => plan.trim() !== "")) ||
          item.isContactus === "Yes"
      );

      container.innerHTML = `
  <div class="pricing-widget-container">
    ${validData
      ?.map((plan, index) => {
        const isContactus = plan.isContactus === "Yes";
        const isFree = plan.price == 0;
        const isPromoActive = PromoPlan === "Yes" && plan.isPromoPrice === "Yes";

        const showINR = isFromIndia && Boolean(plan?.priceINR);

        const activeSpecialOfferText = showINR
          ? plan.specialOfferTextINR ||
            plan.SpecialOfferTextINR ||
            plan.SpecialOfferText ||
            ""
          : plan.SpecialOfferText || "";

        const activeUSDPrice = isPromoActive ? plan.price : (plan.MRPprice ?? plan.price);
        const activeUSDSubPrice = isPromoActive
          ? plan.subPrice
          : (plan.MRPsubPrice ?? plan.subPrice);

        const activeINRPrice = isPromoActive
          ? (plan.priceINR ?? plan.price)
          : (plan.mrpINR ?? plan.MRPpriceINR ?? plan.priceINR ?? plan.price);
        const activeINRSubPrice = isPromoActive
          ? (plan.subPriceINR ?? plan.subPrice)
          : (plan.mrpSubPriceINR ??
            plan.MRPsubPriceINR ??
            plan.subPriceINR ??
            plan.subPrice);

        const inrMRPToDisplay =
          plan.mrpINR ?? plan.MRPpriceINR ?? plan.MRPPriceINR ?? 0;
        const inrMRPSubToDisplay =
          plan.mrpSubPriceINR ??
          plan.MRPsubPriceINR ??
          plan.MRPsubpriceINR ??
          "";

        const displayINRPrice = Number(activeINRPrice).toLocaleString("en-IN");

        return `
           <div class="plan-card ${
             validData?.some((x) => x.isPromoPrice) && PromoPlan === "Yes"
               ? " bigPadding MRPCardStyles"
               : " smallPadding"
           } 
    ${plan.MRPprice && PromoPlan === "Yes" ? " paddingStylesOfCard" : ""}
    ${
      index === validData.length - 1 &&
      plan.MRPprice &&
      PromoPlan === "Yes" &&
      isPromoActive
        ? " EnterPriseCard"
        : index === validData.length - 1
        ? "featured"
        : ""
    }
    ${
      !plan.MRPprice &&
      plan.title &&
      PromoPlan === "Yes" &&
      isPromoActive &&
      plan.title === "Enterprise"
        ? " EnterpriseCardColor"
        : ""
    }">
       ${
         activeSpecialOfferText && PromoPlan === "Yes" && isPromoActive
           ? `<div class="save-badge"><span class="Promo-Offtext">${activeSpecialOfferText}</span><i class="LimitedOffer"> Limited time offer*</i></div>`
           : ""
       }
            <h4 class="plan-title">${plan.title}</h4>

              <div class=${
                isPromoActive && (plan.MRPprice || inrMRPToDisplay)
                  ? "FlexStyles"
                  : "plan-price"
              }>
         ${
           isContactus
             ? `<div class="customPricetext">${plan.ContactusDescription || "Contact Us"}</div>`
             : isFree
             ? `<div>Free</div>`
             : isPromoActive && (plan.MRPprice || inrMRPToDisplay)
             ? showINR
               ? `
                  <div class="ActualPriceandPlanPrice">
                    <div class="ActualPrice">
                      &#8377;${Number(inrMRPToDisplay).toLocaleString("en-IN")} 
                      <div class="SubPriceUI">${inrMRPSubToDisplay}</div>
                      <span class="Borderline"></span>
                    </div>
                    <div class="PremiumPriceStyles">
                      &#8377;${Number(plan.priceINR).toLocaleString("en-IN")} 
                      <div class="SubPriceUI">${plan.subPriceINR ?? ""}</div>
                    </div>
                  </div>
                `
               : `
                  <div class="ActualPriceandPlanPrice">
                    <div class="ActualPrice">
                      $${plan.MRPprice} 
                      <div class="SubPriceUI">${plan.MRPsubPrice ?? ""}</div>
                      <span class="Borderline"></span>
                    </div>
                    <div class="PremiumPriceStyles">
                      $${plan.price} 
                      <div class="SubPriceUI">${plan.subPrice ?? ""}</div>
                    </div>
                  </div>
                `
             : showINR
             ? `
                  <div class="rupee-price-wrapper">
                    <span class="rupee-price-icon">&#8377;</span>
                    <span class="rupee-price">${displayINRPrice}</span>
                  </div>
                  <div class="SubPriceUI">${activeINRSubPrice ?? ""}</div>
                `
             : `
                  <div>$${activeUSDPrice}</div>
                  <div class="SubPriceUI">${activeUSDSubPrice ?? ""}</div>
                `
         }
        </div>

            ${
              !isContactus
                ? `<p class="billing-info" style="visibility:${
                    isFree || isContactus ? "hidden" : "visible"
                  };border-bottom:${
                    LiteUserBased === "Yes" ? "0.15vw solid #ddd" : "0px solid #ddd"
                  };">
                    ${
                      UserBased === "Yes"
                        ? `per user / month, billed yearly`
                        : `per month, billed yearly`
                    }
                  </p>`
                : ""
            }
 ${
   plan.SpecialDescription &&
   (plan.MRPprice || inrMRPToDisplay) &&
   PromoPlan === "Yes" &&
   isPromoActive
     ? `<p class="special-description">${plan.SpecialDescription}</p>`
     : ""
 }
            ${
              LiteUserBased === "Yes" && !isContactus
                ? `<div>
                    <div class="plan-price">
                      ${
                        plan.LiteUserprice == 0
                          ? `<div>Free</div>`
                          : !isFromIndia
                          ? `<div>$${plan.LiteUserprice}</div>
                             <div class="SubPriceUI">${plan.LiteUsersubPrice ?? ""}</div>`
                          : plan.LiteUserpriceINR
                          ? `<div class="rupee-price-wrapper">
                               <span class="rupee-price-icon">&#8377;</span>
                               <span class="rupee-price">${Number(plan.LiteUserpriceINR).toLocaleString("en-IN")}</span>
                             </div>
                             <div class="SubPriceUI">${plan.LiteUsersubPriceINR ?? ""}</div>`
                          : `<div>$${plan.LiteUserprice}</div>
                             <div class="SubPriceUI">${plan.LiteUsersubPrice ?? ""}</div>`
                      }
                    </div>
                    <p class="billing-info" style="visibility:${
                      plan.LiteUserprice == 0 ? "hidden" : "visible"
                    };">
                      ${
                        UserBased === "Yes"
                          ? `per lite user / month, billed yearly`
                          : `per month, billed yearly`
                      }
                    </p>
                  </div>`
                : ""
            }

            <h3 class="plan-features-title">
              ${
                index > 0
                  ? "Everything in " + validData[index - 1].title + " and..."
                  : (validData?.[index]?.title || "") + " Plan Features"
              }
            </h3>

            <ul class="plan-features">
  ${plan.plans
    .map((feature) => {
      const displayFeature = formatDynamicFeatureText(feature, plan);
      return displayFeature
        ? `<li>
            <span class="tick-icon">✔ </span>

            <span class="helpdesktoolTipStyles">
              ${displayFeature}

              ${
                plans.ProductName === "Helpdesk 365" &&
                (feature === "Minimum 5 users" ||
                  feature.toLowerCase().includes("ticket requesters"))
                  ? `
                    <span class="custom-tooltip">

                <img 
  src="${
    plan.title === "Enterprise" && isPromoActive
      ? "https://ik.imagekit.io/apps365/Lp-pages/info%20-icon-black-svg_.png"
      : plan.title === "Enterprise"
      ? "https://ik.imagekit.io/apps365/info%20-icon-white.png"
      : "https://ik.imagekit.io/apps365/Lp-pages/info%20-icon-black-svg_.png"
  }"
  alt="info"
  class="info-image"
/>

                      <span class="tooltip-text">
                        ${
                          feature === "Minimum 5 users"
                            ? "These are your helpdesk agents (Admins, Supervisors, Agents). Pricing is based on how many agents manage tickets."
                            : "Number of users who can raise and track tickets (not a ticket limit)."
                        }
                      </span>

                    </span>
                  `
                  : ""
              }

            </span>
          </li>`
        : "";
    })
    .join("")}
</ul>
            <div class="custom-elementor-shortcode">
              <button class="buy-price ${
                index === validData.length - 1 &&
                (!plan.MRPprice || PromoPlan !== "Yes" || !isPromoActive)
                  ? "CustomAddToCartEnterprise"
                  : "CustomAddToCart"
              }"
                id="${
                  isContactus
                    ? "RequestLicenseEnterprise"
                    : isFree
                    ? "openCustomPricePopup"
                    : `std${index + 1}`
                }"
                data-ids="${3154 + index}">
                <a href="#!" id="std${index + 1}1">
                  ${isContactus || isFree ? "Request License" : "Add To Cart"}
                </a>
              </button>
            </div>
          </div>
        `;
      })
      .join("")}
  </div>
`;

      const requestBtn = document.getElementById("openCustomPricePopup");
      if (requestBtn) {
        requestBtn.addEventListener("click", () => {
          handleOpenpopup();
        });
      }
      const EnterpriseBtn = document.getElementById("RequestLicenseEnterprise");
      if (EnterpriseBtn) {
        EnterpriseBtn.addEventListener("click", () => {
          handleOpenEnterPrisepopup();
        });
      }

      validData.forEach((plan, index) => {
        const addToCartButton = document.getElementById(`std${index + 1}`);
        if (addToCartButton) {
          addToCartButton.addEventListener("click", () =>
            getTxID(plan, LiteUserBased, PromoPlan)
          );
        }
      });
    } catch (err) {
      console.error("getData execution failed:", err);
    }
  }

  function parseAndCombinePlans(data) {
    return [
      { title: "Standard", ...JSON.parse(data.P1Plans || "{}") },
      { title: "Plus", ...JSON.parse(data.P2Plans || "{}") },
      { title: "Premium", ...JSON.parse(data.P3Plans || "{}") },
      { title: "Enterprise", ...JSON.parse(data.P4Plans || "{}") },
    ];
  }

  function decryptFromBase64Url(encryptedText) {
    let secretKey = "Super@Salt";
    let base64 = encryptedText.replace(/-/g, "+").replace(/_/g, "/");
    let decrypted = CryptoJS.AES.decrypt(atob(base64), secretKey);
    return decrypted.toString(CryptoJS.enc.Utf8);
  }

  function encryptToBase64Url(text) {
    let secretKey = "Super@Salt";
    let encrypted = CryptoJS.AES.encrypt(text, secretKey).toString();
    let base64 = btoa(encrypted);
    return base64.replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
  }

  function createProductItem(img, name, description, price, GUID, isINRItem = false) {
    return {
      price: {
        description: description || "",
        billing_cycle: {
          frequency: 1,
          interval: "year",
        },
        tax_mode: "external",
        unit_price: {
          amount: (Number(price) * 100).toString(),
          currency_code: isINRItem ? "INR" : "USD",
        },
        custom_data: {
          selectedYear: "1 year",
          productGuid: GUID || name || "",
        },
        product: {
          name: name || "",
          image_url: img || "",
          tax_category: "saas",
        },
      },
      quantity: 1,
    };
  }

  function isStringValidated(value) {
    return (
      typeof value === "string" &&
      value !== null &&
      value !== undefined &&
      value !== ""
    );
  }

  function filterUniqueProducts(cartItems) {
    const uniqueProducts = [];
    const seenDescriptions = new Set();

    cartItems?.forEach((item) => {
      const description = item.price.description;
      if (!seenDescriptions.has(description)) {
        uniqueProducts.push(item);
        seenDescriptions.add(description);
      }
    });

    return uniqueProducts;
  }

  function handleOpenpopup() {
    const popup = document.getElementById("customPricePopup");
    if (popup) popup.style.display = "flex";
  }

  function handleOpenEnterPrisepopup() {
    const popup = document.getElementById("customEnterPrisePopup");
    if (popup) popup.style.display = "flex";
  }

  function handleClosepopup() {
    const popup = document.getElementById("customPricePopup");
    if (popup) popup.style.display = "none";
  }

  async function getTxID(plan, LiteUserBased, globalPromoPlan = "No") {
    const useINR = Boolean(isFromIndia && plan?.priceINR);

    const isPromoActive =
      (globalPromoPlan === "Yes" || plan?.PromoPlan === "Yes") &&
      plan?.isPromoPrice === "Yes";

    const activeUSDTotal = isPromoActive
      ? plan?.TotalPrice
      : plan?.MRPTotalPrice ?? plan?.TotalPrice;
    const activeINRTotal = isPromoActive
      ? plan?.TotalPriceINR ?? plan?.priceINR
      : plan?.MRPTotalPriceINR ?? plan?.TotalPriceINR ?? plan?.mrpINR;

    const mainPrice = useINR ? activeINRTotal : activeUSDTotal;

    let staticbody = {
      items:
        isStringValidated(plan?.AddOns?.[0]?.name) &&
        plan?.AddOns?.[0]?.price &&
        plan?.AddOns?.[0]?.price != 0
          ? LiteUserBased === "Yes"
            ? [
                createProductItem(
                  plan?.ProductImage,
                  plan?.ProductTitle,
                  plan?.ProductDescription,
                  mainPrice,
                  plan?.ProductGUID,
                  useINR
                ),
                createProductItem(
                  plan?.ProductImage,
                  plan?.ProductTitleLiteUser,
                  plan?.ProductDescriptionLiteUser,
                  useINR && plan?.LiteUserTotalPriceINR
                    ? plan.LiteUserTotalPriceINR
                    : plan?.LiteUserTotalPrice,
                  plan?.ProductGUID,
                  useINR
                ),
                ...plan?.AddOns?.map((addOn) =>
                  createProductItem(
                    plan?.ProductImage,
                    addOn?.name,
                    addOn?.description,
                    useINR && addOn?.priceINR ? addOn.priceINR : addOn?.price,
                    addOn?.guid,
                    useINR
                  )
                ),
              ]
            : [
                createProductItem(
                  plan?.ProductImage,
                  plan?.ProductTitle,
                  plan?.ProductDescription,
                  mainPrice,
                  plan?.ProductGUID,
                  useINR
                ),
                ...plan?.AddOns?.map((addOn) =>
                  createProductItem(
                    plan?.ProductImage,
                    addOn?.name,
                    addOn?.description,
                    useINR && addOn?.priceINR ? addOn.priceINR : addOn?.price,
                    addOn?.guid,
                    useINR
                  )
                ),
              ]
          : LiteUserBased === "Yes"
          ? [
              createProductItem(
                plan?.ProductImage,
                plan?.ProductTitle,
                plan?.ProductDescription,
                mainPrice,
                plan?.ProductGUID,
                useINR
              ),
              createProductItem(
                plan?.ProductImage,
                plan?.ProductTitleLiteUser,
                plan?.ProductDescriptionLiteUser,
                useINR && plan?.LiteUserTotalPriceINR
                  ? plan.LiteUserTotalPriceINR
                  : plan?.LiteUserTotalPrice,
                plan?.ProductGUID,
                useINR
              ),
            ]
          : [
              createProductItem(
                plan?.ProductImage,
                plan?.ProductTitle,
                plan?.ProductDescription,
                mainPrice,
                plan?.ProductGUID,
                useINR
              ),
            ],
    };

    let Existingcart = localStorage.getItem("storedUpdatedAppURL")
      ? JSON.parse(
          decryptFromBase64Url(localStorage.getItem("storedUpdatedAppURL"))
        )
      : [];

    let currency_code = useINR ? "INR" : "USD";
    let currency_symbol = useINR ? "₹" : "$";
    let userInfo = Existingcart?.userInfo || {};

    let finalCartItems = [];
    if (
      Existingcart &&
      Existingcart.currency_code === currency_code &&
      Array.isArray(Existingcart.items)
    ) {
      finalCartItems = Existingcart.items.concat(staticbody.items);
    } else {
      finalCartItems = staticbody.items;
    }

    const uniqueCartItems = filterUniqueProducts(finalCartItems);
    let URL = JSON.stringify({
      items: uniqueCartItems,
      currency_code: currency_code,
      currency_symbol: currency_symbol,
      currency: currency_code,
      userInfo: userInfo,
    });

    let nextpageURL = encryptToBase64Url(URL);
    localStorage.setItem("storedUpdatedAppURL", nextpageURL);
    localStorage.setItem("storedActualAppURL", nextpageURL);

    let siteurl = "";
    if (SiteName === "HR365") {
      siteurl = "https://www.hr365.us/checkout/";
    } else {
      siteurl = "https://www.apps365.com/checkout/";
    }
    const urlToStore = `${siteurl}?producturl=${nextpageURL}`;
    window.open(urlToStore, "_self");
  }

  return (
    <>
      <div id="pricing-widget-container" data-plan-type="Task 365">
        <div className="loader-wrapper">
          <div className="loader">
            <span></span>
            <span></span>
            <span></span>
            <span></span>
            <span></span>
            <span></span>
            <span></span>
            <span></span>
            <span></span>
            <span></span>
            <span></span>
            <span></span>
          </div>
          <div className="loading-text">Loading Plans...</div>
        </div>
      </div>

      <div
        id="customPricePopup"
        style={{ display: "none" }}
        className="popup-overlay"
      >
        <RequestLicense handleClosepopup={handleClosepopup} />
      </div>

      <div
        id="customEnterPrisePopup"
        style={{ display: "none" }}
        className="popup-overlay"
      >
        <RequestLicenseEnterprise handleClosepopup={handleClosepopup} />
      </div>
    </>
  );
};

export default CustomPlans;